import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Send } from 'lucide-react';
import { toast } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Color palette ── */
const COLORS = [
  { name: 'Gloss Black', hex: '#0a0a0a', dark: '#000000' },
  { name: 'Navy Blue',   hex: '#0f1e5c', dark: '#060e2e' },
  { name: 'Sky Blue',    hex: '#4a9ee6', dark: '#1a4a80' },
  { name: 'Purple',      hex: '#6a1bb5', dark: '#2d0a4e' },
  { name: 'Cherry Red',  hex: '#c81717', dark: '#5a0a0a' },
  { name: 'Yellow',      hex: '#f5c400', dark: '#7a6200' },
  { name: 'Magenta',     hex: '#c71585', dark: '#5a0a3c' },
  { name: 'Lime Green',  hex: '#7ecc47', dark: '#2a5a10' },
  { name: 'Nardo Grey',  hex: '#7d7568', dark: '#3a342e' },
];

const VEHICLES = [
  {
    id: 'sedan', label: 'Sedan / Coupe', img: '/cars/sedan.png',
    partial: { hood: '/cars/sedan_hood.jpg', roof: '/cars/sedan_roof.jpg', trunk: '/cars/sedan_trunk.jpg' },
  },
  {
    id: 'suv', label: 'SUV / Crossover', img: '/cars/suv.png',
    partial: { hood: '/cars/suv_hood.jpg', roof: '/cars/suv_roof.jpg', trunk: '/cars/suv_trunk.jpg' },
  },
  {
    id: 'truck', label: 'Truck / Van', img: '/cars/truck.png',
    partial: { hood: '/cars/truck_hood.jpg', roof: '/cars/truck_roof.jpg', trunk: '/cars/truck_trunk.jpg' },
  },
];

const FINISHES     = ['Gloss', 'Satin', 'Matte'];
const EXTRA_PANELS = ['Hood', 'Roof', 'Trunk', 'Doors', 'Fenders', 'Bumpers'];
const PARTIAL_OPTS = ['hood', 'roof', 'trunk'];

/* ── Car icons ── */
const SedanIcon = () => (
  <svg width="22" height="14" viewBox="0 0 22 14" fill="currentColor">
    <path d="M14.5 2.5L12 0H7L4.5 2.5H1.5L0 5v4h1.5a2.5 2.5 0 005 0h9a2.5 2.5 0 005 0H22V7l-2-2h-5L14.5 2.5zM4 10a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm14 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
  </svg>
);
const SuvIcon = () => (
  <svg width="24" height="14" viewBox="0 0 24 14" fill="currentColor">
    <path d="M16 0H6L3 4H1.5L0 6v5h2a2.5 2.5 0 005 0h10a2.5 2.5 0 005 0h2V8l-2-3h-3L16 0zM4.5 12a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm15 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
  </svg>
);
const TruckIcon = () => (
  <svg width="24" height="14" viewBox="0 0 24 14" fill="currentColor">
    <path d="M14 0H1L0 4v7h2a2.5 2.5 0 005 0h7v-2h6l3-4V7l-1-3h-5L14 0zm-8.5 12a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm14 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM16 4h4.5l2 3H16V4z"/>
  </svg>
);
const VEHICLE_ICONS = { sedan: SedanIcon, suv: SuvIcon, truck: TruckIcon };

/* ── Reliable 2-layer CSS crossfade ──
   prev: old image, stays fully visible as backdrop
   curr: new image, fades from 0→1 over 1.2s
   After 1400ms the prev layer is removed.
── */
function CrossfadeImage({ src, objectFit = 'contain' }) {
  const [prev, setPrev]   = useState(null);
  const [curr, setCurr]   = useState(src);
  const [ready, setReady] = useState(true);
  const timerRef = useRef(null);
  const rafRef   = useRef(null);

  useEffect(() => {
    if (src === curr) return;

    clearTimeout(timerRef.current);
    cancelAnimationFrame(rafRef.current);

    setPrev(curr);
    setCurr(src);
    setReady(false);

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setReady(true));
    });

    timerRef.current = setTimeout(() => setPrev(null), 1400);

    return () => {
      clearTimeout(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <>
      {prev && (
        <img src={prev} alt="" draggable={false} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit, zIndex: 1, opacity: 1,
          userSelect: 'none', pointerEvents: 'none',
        }}/>
      )}
      <img
        src={curr}
        alt="Vehicle wrap preview"
        draggable={false}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit, zIndex: 2,
          opacity: ready ? 1 : 0,
          transition: ready
            ? 'opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none',
          userSelect: 'none', pointerEvents: 'none',
        }}
      />
    </>
  );
}

/* ── Inline spinner (no external dep) ── */
function Spinner({ size = 14, color = '#eab308' }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid ${color}`,
      borderTopColor: 'transparent',
      animation: 'spin 0.8s linear infinite',
      flexShrink: 0,
    }}/>
  );
}

/* ── Quote Modal ── */
function QuoteModal({ color, vehicle, coverage, partialSection, onClose }) {
  const [form, setForm]           = useState({ name: '', email: '', phone: '', year: '', make: '', model: '', message: '' });
  const [finish, setFinish]       = useState('Gloss');
  const [extraPanels, setExtras]  = useState([]);
  const [submitting, setSub]      = useState(false);
  const [done, setDone]           = useState(false);

  const wrap = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error('Please fill name, phone, and email.'); return; }
    setSub(true);
    try {
      const coverageStr = coverage === 'Partial'
        ? `Partial — ${partialSection}${extraPanels.length ? ' + ' + extraPanels.join(', ') : ''}`
        : 'Full Body';
      const vehicleStr = [form.year, form.make, form.model].filter(Boolean).join(' ');
      await axios.post(`${BACKEND_URL}/api/vinyl-wrap-quotes`, {
        name: form.name, phone: form.phone, email: form.email,
        color: color.name, finish, coverage: coverageStr,
        message: [vehicleStr ? `Vehicle: ${vehicleStr}` : '', `Style: ${vehicle.label}`, form.message].filter(Boolean).join('\n'),
      });
      setDone(true);
    } catch { toast.error('Failed to send. Please try again.'); }
    finally { setSub(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div
        className="w-full max-w-lg overflow-hidden overflow-y-auto"
        style={{ background: '#111', border: '1px solid #27272a', borderRadius: '24px 24px 0 0', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 14, paddingBottom: 6 }}>
          <div style={{ width: 44, height: 4, borderRadius: 999, background: '#3f3f46' }}/>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px 12px' }}>
          <h2 style={{ color: '#fff', fontWeight: 900, fontSize: 20, margin: 0 }}>Request a Quote</h2>
          <button onClick={onClose} data-testid="quote-modal-close"
            style={{ width: 32, height: 32, borderRadius: '50%', background: '#27272a', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X style={{ width: 16, height: 16, color: '#fff' }}/>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 20px 16px' }}>
          {[color.name, coverage === 'Partial' ? `Partial (${partialSection})` : 'Full Body', vehicle.label].map(chip => (
            <span key={chip} style={{ padding: '4px 12px', borderRadius: 999, background: '#1a1204', border: '1px solid rgba(234,179,8,0.4)', color: '#eab308', fontSize: 12, fontWeight: 600 }}>{chip}</span>
          ))}
        </div>
        <div style={{ width: '100%', height: 1, background: '#27272a' }}/>

        {done ? (
          <div style={{ padding: '32px 20px 40px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Send style={{ width: 28, height: 28, color: '#22c55e' }}/>
            </div>
            <p style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Request Sent!</p>
            <p style={{ color: '#71717a', fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>We'll review your request and email you a personalized quote shortly.</p>
            <button onClick={onClose} style={{ width: '100%', padding: '14px', borderRadius: 16, background: '#eab308', border: 'none', color: '#000', fontWeight: 900, fontSize: 15, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '20px 20px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Contact Information</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[{k:'name',t:'text',p:'Full Name *'},{k:'phone',t:'tel',p:'Phone *'},{k:'email',t:'email',p:'Email *'}].map(f => (
                  <input key={f.k} type={f.t} placeholder={f.p} value={form[f.k]} onChange={wrap(f.k)}
                    data-testid={`quote-${f.k}-input`}
                    style={{ width: '100%', padding: '13px 16px', background: '#18181b', border: '1.5px solid #27272a', borderRadius: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = 'rgba(234,179,8,0.5)'}
                    onBlur={e => e.target.style.borderColor = '#27272a'}
                  />
                ))}
              </div>
            </div>
            <div>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Vehicle</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {[{k:'year',p:'Year'},{k:'make',p:'Make'},{k:'model',p:'Model'}].map(f => (
                  <input key={f.k} type="text" placeholder={f.p} value={form[f.k]} onChange={wrap(f.k)}
                    style={{ width: '100%', padding: '12px 10px', background: '#18181b', border: '1.5px solid #27272a', borderRadius: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = 'rgba(234,179,8,0.5)'}
                    onBlur={e => e.target.style.borderColor = '#27272a'}
                  />
                ))}
              </div>
            </div>
            {coverage === 'Partial' && (
              <div>
                <p style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Add More Panels <span style={{ color: '#71717a', fontWeight: 400 }}>(Optional)</span></p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {EXTRA_PANELS.filter(p => p.toLowerCase() !== partialSection).map(panel => {
                    const on = extraPanels.includes(panel);
                    return (
                      <button key={panel} type="button"
                        onClick={() => setExtras(prev => on ? prev.filter(x => x !== panel) : [...prev, panel])}
                        style={{ padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: `1.5px solid ${on ? '#eab308' : '#27272a'}`, background: on ? '#1a1204' : '#18181b', color: on ? '#eab308' : '#a1a1aa', transition: 'all 0.15s' }}>
                        {panel}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Finish</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {FINISHES.map(f => (
                  <button key={f} type="button" onClick={() => setFinish(f)} data-testid={`finish-${f.toLowerCase()}`}
                    style={{ padding: '13px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 14, border: `1.5px solid ${finish === f ? '#eab308' : '#27272a'}`, background: finish === f ? '#1a1204' : '#18181b', color: finish === f ? '#eab308' : '#fff', transition: 'all 0.15s' }}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <textarea placeholder="Special request or questions… (optional)" value={form.message} onChange={wrap('message')} rows={2}
              style={{ width: '100%', padding: '13px 16px', background: '#18181b', border: '1.5px solid #27272a', borderRadius: 12, color: '#fff', fontSize: 14, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
              onFocus={e => e.target.style.borderColor = 'rgba(234,179,8,0.5)'}
              onBlur={e => e.target.style.borderColor = '#27272a'}
            />
            <button type="submit" disabled={submitting} data-testid="quote-submit-btn"
              style={{ width: '100%', padding: '16px', borderRadius: 16, border: 'none', cursor: 'pointer', background: '#eab308', color: '#000', fontWeight: 900, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: submitting ? 0.6 : 1, transition: 'opacity 0.15s' }}>
              {submitting
                ? <><Spinner size={18} color="#000"/> Sending…</>
                : <><Send style={{ width: 16, height: 16 }}/> Send Request</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function WrapDesignerPage() {
  const navigate = useNavigate();

  const [color,    setColor]    = useState(COLORS[0]);
  const [vehicle,  setVehicle]  = useState(VEHICLES[0]);
  const [coverage, setCoverage] = useState('Full Body');
  const [section,  setSection]  = useState('hood');

  const [stockResults, setStockResults] = useState({});
  const [loadingHex,   setLoadingHex]   = useState(null);
  const [showModal,    setShowModal]    = useState(false);

  const stockKey = useCallback(
    (vId, cvg, sec, hex) => cvg === 'Partial' ? `${vId}_${sec}-${hex}` : `${vId}-${hex}`,
    []
  );

  const vehicleId = useCallback(
    (vId, cvg, sec) => cvg === 'Partial' ? `${vId}_${sec}` : vId,
    []
  );

  const aiResult   = stockResults[stockKey(vehicle.id, coverage, section, color.hex)];
  const baseImg    = coverage === 'Partial' && vehicle.partial?.[section]
    ? vehicle.partial[section]
    : vehicle.img;
  const displaySrc = aiResult ? `data:image/jpeg;base64,${aiResult}` : baseImg;
  const isLoading  = loadingHex === color.hex;

  const prefetchedRef = useRef(new Set());

  /* Prefetch all 9 colors for current vehicle — full-body mode */
  useEffect(() => {
    if (coverage === 'Partial') return;
    COLORS.forEach(c => {
      const k = `${vehicle.id}-${c.hex}`;
      if (prefetchedRef.current.has(k)) return;
      prefetchedRef.current.add(k);
      axios.post(`${BACKEND_URL}/api/wrap/generate`, {
        vehicle: vehicle.id, color_name: c.name, color_hex: c.hex,
        mode: 'full_body', preserve_background: false,
      }, { timeout: 90000 })
        .then(res => {
          if (res.data.image) setStockResults(prev => prev[k] ? prev : { ...prev, [k]: res.data.image });
        })
        .catch(() => {});
    });
  }, [vehicle.id, coverage]);

  /* Prefetch all 9 colors for current vehicle+section — partial mode */
  useEffect(() => {
    if (coverage !== 'Partial') return;
    const vId = `${vehicle.id}_${section}`;
    COLORS.forEach(c => {
      const k = `${vehicle.id}_${section}-${c.hex}`;
      if (prefetchedRef.current.has(k)) return;
      prefetchedRef.current.add(k);
      axios.post(`${BACKEND_URL}/api/wrap/generate`, {
        vehicle: vId, color_name: c.name, color_hex: c.hex,
        mode: 'partial', section, preserve_background: false,
      }, { timeout: 90000 })
        .then(res => {
          if (res.data.image) setStockResults(prev => prev[k] ? prev : { ...prev, [k]: res.data.image });
        })
        .catch(() => {});
    });
  }, [vehicle.id, section, coverage]);

  /* Tap a color swatch */
  const handleColorTap = useCallback(async (c) => {
    setColor(c);
    const vId = vehicleId(vehicle.id, coverage, section);
    const k   = stockKey(vehicle.id, coverage, section, c.hex);
    if (stockResults[k]) return; // already cached — crossfade handles the visual
    setLoadingHex(c.hex);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/wrap/generate`, {
        vehicle: vId, color_name: c.name, color_hex: c.hex,
        mode: coverage === 'Partial' ? 'partial' : 'full_body',
        section: coverage === 'Partial' ? section : null,
        preserve_background: false,
      }, { timeout: 90000 });
      if (res.data.image) setStockResults(prev => ({ ...prev, [k]: res.data.image }));
      else toast.error('Generation failed. Please retry.');
    } catch { toast.error('Could not generate preview.'); }
    finally { setLoadingHex(null); }
  }, [vehicle.id, coverage, section, stockResults, stockKey, vehicleId]);

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#fff' }}>

      {/* Sticky Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: '#000', borderBottom: '1px solid #18181b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '14px 16px 12px',
      }}>
        <button onClick={() => navigate('/')} data-testid="wrap-back-btn"
          style={{ position: 'absolute', left: 16, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}>
          <ArrowLeft style={{ width: 24, height: 24, color: '#eab308' }}/>
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: 17, letterSpacing: '0.02em' }}>Vinyl Wrap</div>
          <div style={{ color: '#71717a', fontSize: 12, marginTop: 1 }}>Design your quote</div>
        </div>
      </div>

      {/* Sticky Preview Card + Swatches */}
      <div style={{ position: 'sticky', top: 55, zIndex: 40, background: '#FFFFFF' }}>
        <div style={{ position: 'relative', width: '100%', paddingTop: '72%', overflow: 'hidden' }}>
          <CrossfadeImage src={displaySrc} objectFit="contain" />

          {/* Small bottom-left spinner during generation */}
          {isLoading && (
            <div style={{
              position: 'absolute', bottom: 10, left: 10, zIndex: 12,
              background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
              borderRadius: 999, padding: '6px 10px',
              display: 'flex', alignItems: 'center', gap: 7,
              pointerEvents: 'none',
            }}>
              <Spinner size={14} color="#eab308"/>
            </div>
          )}
        </div>

        {/* Sticky Color Swatches */}
        <div style={{
          background: '#000', borderBottom: '1px solid #1a1a1a',
          padding: '12px 16px',
          display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center',
          overflowX: 'auto',
        }}>
          {COLORS.map(c => {
            const isSel = color.hex === c.hex;
            const sz    = isSel ? 30 : 24;
            return (
              <button
                key={c.hex}
                title={c.name}
                data-testid={`color-swatch-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => handleColorTap(c)}
                style={{
                  width: sz, height: sz, borderRadius: '50%', border: 'none', padding: 0,
                  flexShrink: 0, cursor: 'pointer', position: 'relative',
                  background: [
                    `radial-gradient(circle at 32% 22%, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.06) 30%, transparent 52%)`,
                    `radial-gradient(circle at 70% 78%, ${c.dark} 0%, transparent 50%)`,
                    c.hex,
                  ].join(', '),
                  boxShadow: isSel
                    ? `0 0 0 2.5px #000, 0 0 0 4px #eab308, 0 4px 14px ${c.hex}88`
                    : `inset 0 -3px 8px rgba(0,0,0,0.40), inset 0 2px 5px rgba(255,255,255,0.20), 0 2px 8px rgba(0,0,0,0.35)`,
                  transition: 'width 0.15s ease, height 0.15s ease, box-shadow 0.15s ease',
                }}>
                {loadingHex === c.hex && (
                  <div style={{
                    position: 'absolute', inset: -4, borderRadius: '50%',
                    border: '2px solid #eab308',
                    animation: 'spin 1s linear infinite',
                    pointerEvents: 'none',
                  }}/>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable Controls */}
      <div style={{ background: '#000', padding: '18px 16px 120px', position: 'relative', zIndex: 10 }}>

        {/* Choose Vehicle Style */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ color: '#fff', fontWeight: 900, fontSize: 15 }}>Choose Vehicle Style</span>
            <span style={{ color: '#eab308', fontSize: 12, fontWeight: 600 }}>Drives the preview</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {VEHICLES.map(v => {
              const Icon = VEHICLE_ICONS[v.id];
              const sel  = vehicle.id === v.id;
              return (
                <button key={v.id} data-testid={`vehicle-style-${v.id}`}
                  onClick={() => setVehicle(v)}
                  style={{
                    padding: '12px 8px', borderRadius: 12, cursor: 'pointer',
                    border: `1.5px solid ${sel ? '#eab308' : '#27272a'}`,
                    background: sel ? '#1a1204' : '#111',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}>
                  <span style={{ color: sel ? '#eab308' : '#71717a' }}><Icon/></span>
                  <span style={{ color: sel ? '#eab308' : '#a1a1aa', fontSize: 11, fontWeight: 700, textAlign: 'center', lineHeight: 1.3 }}>
                    {v.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Select Option */}
        <div style={{ marginBottom: 22 }}>
          <p style={{ color: '#fff', fontWeight: 900, fontSize: 15, marginBottom: 10 }}>Select Option</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {['Full Body', 'Partial'].map(opt => {
              const sel = coverage === opt;
              return (
                <button key={opt} data-testid={`coverage-${opt.replace(' ', '-').toLowerCase()}`}
                  onClick={() => setCoverage(opt)}
                  style={{
                    padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                    border: `1.5px solid ${sel ? '#eab308' : '#27272a'}`,
                    background: sel ? '#1a1204' : '#111',
                    color: sel ? '#eab308' : '#fff',
                    fontWeight: 700, fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}>
                  {opt === 'Full Body'
                    ? <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor"><path d="M12 2.5L10 0H6L4 2.5H2L0 5v4h1.5a2 2 0 004 0h7a2 2 0 004 0H18V7l-1.5-2H12V2.5z"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="7" height="7" rx="1.5"/><rect x="9" y="0" width="7" height="7" rx="1.5"/><rect x="0" y="9" width="7" height="7" rx="1.5"/><rect x="9" y="9" width="7" height="7" rx="1.5" fillOpacity="0.35"/></svg>
                  }
                  {opt}
                </button>
              );
            })}
          </div>

          {coverage === 'Partial' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
              {PARTIAL_OPTS.map(s => (
                <button key={s} data-testid={`partial-section-${s}`}
                  onClick={() => setSection(s)}
                  style={{
                    padding: '12px 8px', borderRadius: 12, cursor: 'pointer', textTransform: 'capitalize',
                    border: `1.5px solid ${section === s ? '#eab308' : '#27272a'}`,
                    background: section === s ? '#1a1204' : '#111',
                    color: section === s ? '#eab308' : '#a1a1aa',
                    fontWeight: 700, fontSize: 13,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* CONTINUE */}
        <button data-testid="wrap-continue-btn" onClick={() => setShowModal(true)}
          style={{
            width: '100%', padding: '18px 16px', borderRadius: 14,
            background: '#eab308', border: 'none', color: '#000',
            fontWeight: 900, fontSize: 15, letterSpacing: '0.06em', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxShadow: '0 4px 24px rgba(234,179,8,0.28)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#d4a017'}
          onMouseLeave={e => e.currentTarget.style.background = '#eab308'}
        >
          CONTINUE →
        </button>

        <p style={{ color: '#52525b', fontSize: 12, textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
          We'll email you a personalized price. No deposit charged until you approve.
        </p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {showModal && (
        <QuoteModal color={color} vehicle={vehicle} coverage={coverage} partialSection={section} onClose={() => setShowModal(false)}/>
      )}
    </div>
  );
}
